/**
 * One log sheet for the whole phone app.
 *
 * Every screen has a reason to open it — the + in the tab bar, a project's "Log
 * time", a task's row, a logged entry that needs correcting — and a sheet per
 * screen would mean four copies of the same seven fields, each drifting from
 * the others. This holds the single instance above the router so it survives
 * navigation and closes to wherever you were.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { LogSheet, type LogSheetRequest } from "./LogSheet";
import { RunningSheet } from "./RunningSheet";

interface LogSheetValue {
  open: (request?: LogSheetRequest) => void;
  /** The running timer's sheet — from the timer bar or the running card. */
  openTimer: () => void;
}

const Ctx = createContext<LogSheetValue | null>(null);

export function LogSheetProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<LogSheetRequest | null>(null);
  const [timerOpen, setTimerOpen] = useState(false);

  const open = useCallback((next: LogSheetRequest = {}) => setRequest(next), []);
  const openTimer = useCallback(() => setTimerOpen(true), []);
  const value = useMemo<LogSheetValue>(() => ({ open, openTimer }), [open, openTimer]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {request && (
        // Keyed so reopening for a different entry starts from that entry's
        // values rather than the last one's.
        <LogSheet
          key={request.entry?.id ?? `new:${request.projectId ?? ""}:${request.itemId ?? ""}:${request.start ?? ""}`}
          request={request}
          onClose={() => setRequest(null)}
        />
      )}
      {timerOpen && <RunningSheet onClose={() => setTimerOpen(false)} />}
    </Ctx.Provider>
  );
}

export function useLogSheet(): LogSheetValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLogSheet must be used within LogSheetProvider");
  return ctx;
}
